import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Text
} from 'components/ui';
import DefaultPaymentCheckbox from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/DefaultPaymentCheckbox';
import {
    colors, fontSizes, fontWeights, lineHeights, mediaQueries, space
} from 'style/config';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { ALTERNATE_PAYMENT_TYPES } from 'constants/frictionlessCheckout';

const GooglePayExpandedView = ({
    googlePayHeader = 'Pay with Google Pay',
    googlePayLegalNotice = null,
    googlePaySaveAndContinue = 'Save and Continue',
    googlePayCancel = 'Cancel',
    onSaveAndContinue,
    onCancel,
    isAnonymous
}) => {
    const headerRef = useRef(null);

    useEffect(() => {
        headerRef.current?.focus();

        FrictionlessCheckoutBindings.setAlternatePaymentExpandedViewAnalytics('googlepay');
    }, []);

    return (
        <Box
            role='region'
            aria-labelledby='googlepay-expanded-header'
            data-testid='googlepay-expanded-view'
        >
            <Text
                as='h3'
                id='googlepay-expanded-header'
                ref={headerRef}
                tabIndex={-1}
                css={styles.header}
            >
                {googlePayHeader}
            </Text>
            {!isAnonymous && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox
                        paymentName={ALTERNATE_PAYMENT_TYPES.GOOGLE_PAY}
                        useSelectedDefaultOnly
                    />
                </Box>
            )}
            {googlePayLegalNotice && (
                <Text
                    is='p'
                    css={styles.legalNotice}
                    dangerouslySetInnerHTML={{ __html: googlePayLegalNotice }}
                />
            )}
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='googlepay-cancel'
                    aria-label='Cancel Google Pay selection and return'
                >
                    {googlePayCancel}
                </Button>
                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='googlepay-save-continue'
                    aria-label='Save Google Pay and return to payment options'
                >
                    {googlePaySaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.base,
        marginTop: space[3],
        marginBottom: space[1]
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    legalNotice: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.base,
        marginBottom: space[3],
        marginTop: space[2],
        '& a': {
            textDecoration: 'underline',
            color: colors.blue
        }
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray,
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block'
        }
    },
    buttonContainer: {
        display: 'flex',
        // Mobile (SMUI): save-primary sits above cancel-secondary via column-reverse
        // so the DOM order stays cancel → save (matches desktop) but the paint order
        // puts the primary CTA on top. Desktop (LGUI) uses row with cancel on the
        // left. Mirrors the Venmo expanded view's responsive pattern.
        flexDirection: 'column-reverse',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

GooglePayExpandedView.propTypes = {
    googlePayHeader: PropTypes.string,
    googlePayLegalNotice: PropTypes.string,
    googlePaySaveAndContinue: PropTypes.string,
    googlePayCancel: PropTypes.string,
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isAnonymous: PropTypes.bool
};

export default wrapFunctionalComponent(GooglePayExpandedView, 'GooglePayExpandedView');
